import { StatusBar, Style } from '@capacitor/status-bar';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { setEnableDebugLog, setEnableGeolocationLog } from '../App';
import { getScheduleEndTime } from '../api/dms/systemConfiguration.api';
import { logConsole, logTransaction } from '../common/log';
import { getStateDateAsString, getStateMomentAsString, handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigBool, updateConfigString } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';

interface ISettings {
  isDarkMode: boolean;
  enableOfflineMode: boolean;
  // If debug log is enabled
  enableDebugLog: boolean;
  // If geolocation data should be logged
  enableGeolocationLog: boolean;
  // Whether or not GPS services are available on the device. If GPS services fail, this will be set to false
  gpsServicesAvailable: boolean;
  // Whether or not user has activated distance tracking
  distanceTrackingUserActivated: boolean;
  // Whether or not user has paused distance tracking
  distanceTrackingUserPaused: boolean;
  // True when the GPS location has started watching but has not yet received a valid location
  gpsWatchStarting: boolean;
  // True when the GPS location has started watching and has received a valid location
  gpsWatchStarted: boolean;
  // False when the user has manually set GPS services to be disabled, true by default
  gpsServicesUserEnabled: boolean;
  scheduledEndTime: string;
  scheduledStartEndTimeLoading: boolean;
}

const initialState: ISettings = {
  isDarkMode: false,
  gpsServicesAvailable: false,
  enableDebugLog: false,
  enableGeolocationLog: false,
  enableOfflineMode: true,
  distanceTrackingUserActivated: false,
  distanceTrackingUserPaused: false,
  gpsWatchStarting: false,
  gpsWatchStarted: false,
  gpsServicesUserEnabled: true,
  scheduledEndTime: getStateDateAsString(new Date()),
  scheduledStartEndTimeLoading: false,
};

/// THUNKS

export const setIsDarkModeStoreAsync = createAsyncThunk<{}, boolean>('settings/setIsDarkModeStoreAsync', async (isDarkMode, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting is dark mode '${isDarkMode}' to storage`, '[settings] setIsDarkModeStoreAsync');
  try {
    dispatch(setIsDarkModeState(isDarkMode));
    await updateConfigBool(ConfigName.SettingsIsDarkMode, isDarkMode, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setEnableOfflineModeStoreAsync = createAsyncThunk<{}, boolean>('settings/setEnableOfflineModeStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting enable offline mode '${val}' to storage`, '[settings] setEnableOfflineModeStoreAsync');
  try {
    dispatch(setEnableOfflineModeState(val));
    await updateConfigBool(ConfigName.SettingsEnableOfflineMode, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchScheduledStartEndTimeStoreAsync = createAsyncThunk<{}, void>('settings/fetchScheduledStartTimeStoreAsync', async (nil, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Fetching scheduled start time from database`, '[settings] fetchScheduledStartTimeStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setScheduledStartEndTimeLoadingState(true));

    const scheduledEndTimeData = await getScheduleEndTime();

    if (scheduledEndTimeData.value) {
      const scheduledEndTime = getStateMomentAsString(moment(scheduledEndTimeData.value, 'D/MM/YYYY h:mm:ss A'));
      await dispatch(setScheduledEndTimeState(scheduledEndTime));
      await updateConfigString(ConfigName.SettingsScheduledEndTime, scheduledEndTime, false);
    }

    dispatch(setIsLoadingCountState(-1));
    dispatch(setScheduledStartEndTimeLoadingState(false));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setScheduledStartEndTimeLoadingState(false));
  }
});

export const setGpsServicesUserEnabledStoreAsync = createAsyncThunk<{}, boolean>('settings/setGpsServicesUserEnabledStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting GPS services user enabled '${val}' to storage`, '[settings] setGpsServicesUserEnabledStoreAsync');
  try {
    dispatch(setGpsServicesUserEnabledState(val));
    await updateConfigBool(ConfigName.SettingsGpsServicesUserEnabled, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setDistanceTrackingUserActivatedStoreAsync = createAsyncThunk<{}, boolean>('settings/setDistanceTrackingUserActivatedStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting distance tracking user activated '${val}' to storage`, '[settings] setDistanceTrackingUserActivatedStoreAsync');
  try {
    dispatch(setDistanceTrackingUserActivatedState(val));
    await updateConfigBool(ConfigName.SettingsDistanceTrackingUserActivated, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setDistanceTrackingUserPausedStoreAsync = createAsyncThunk<{}, boolean>('settings/setDistanceTrackingUserPausedStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting distance tracking user paused '${val}' to storage`, '[settings] setDistanceTrackingUserPausedStoreAsync');
  try {
    dispatch(setDistanceTrackingUserPausedState(val));
    await updateConfigBool(ConfigName.SettingsDistanceTrackingUserActivated, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setEnableGeolocationLogStoreAsync = createAsyncThunk<{}, boolean>('settings/setEnableGeolocationLogStoreAsync', async (enable, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting enable geolocation log '${enable}' to storage`, '[settings] setEnableGeolocationLogStoreAsync');
  try {
    dispatch(setEnableGeolocationLogState(enable));
    await updateConfigBool(ConfigName.SettingsGeolocationLog, enable, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettingsState: (state, action: PayloadAction<ISettings>) => {
      logConsole(LogType.Info, '[settings] setSettingsState');
      return action.payload;
    },
    setIsDarkModeState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setIsDarkModeState: ${action.payload}`);
      state.isDarkMode = action.payload;

      const applyDom = async () => {
        if (action.payload) {
          document.body.classList.add('dark');
          try {
            await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
            await StatusBar.setBackgroundColor({ color: '#042846' });
          } catch (ex) {}
        } else {
          document.body.classList.remove('dark');
          try {
            await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
            await StatusBar.setBackgroundColor({ color: '#68b8fc' });
          } catch (ex) {}
        }
      };

      applyDom();
    },
    setEnableDebugLogState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setEnableDebugLogState: ${action.payload}`);
      state.enableDebugLog = action.payload;
      setEnableDebugLog(action.payload);
    },
    setEnableGeolocationLogState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setEnableGeolocationLogState: ${action.payload}`);
      state.enableGeolocationLog = action.payload;
      setEnableGeolocationLog(action.payload);
    },
    setGpsServicesAvailableState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setGpsServicesAvailableState: ${action.payload}`);
      state.gpsServicesAvailable = action.payload;
    },
    setDistanceTrackingUserActivatedState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setDistanceTrackingUserActivatedState: ${action.payload}`);
      state.distanceTrackingUserPaused = false;
      state.distanceTrackingUserActivated = action.payload;
    },
    setDistanceTrackingUserPausedState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setDistanceTrackingUserPausedState: ${action.payload}`);
      state.distanceTrackingUserPaused = action.payload;
    },
    setGpsWatchStartingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setGpsWatchStartingState: ${action.payload}`);
      state.gpsWatchStarting = action.payload;
    },
    setGpsWatchStartedState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setGpsWatchStartedState: ${action.payload}`);
      state.gpsWatchStarted = action.payload;
    },
    setGpsServicesUserEnabledState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setGpsServicesUserEnabledState: ${action.payload}`);
      state.gpsServicesUserEnabled = action.payload;
    },
    setEnableOfflineModeState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setEnableOfflineModeState: ${action.payload}`);
      state.enableOfflineMode = action.payload;
    },
    // setScheduledStartTimeState: (state, action: PayloadAction<string>) => {
    //   logConsole(LogType.Info, `[settings] setScheduledStartTimeState: ${action.payload}`);
    //   state.scheduledStartTime = action.payload;
    // },
    setScheduledEndTimeState: (state, action: PayloadAction<string>) => {
      logConsole(LogType.Info, `[settings] setScheduledEndTimeState: ${action.payload}`);
      state.scheduledEndTime = action.payload;
    },
    setScheduledStartEndTimeLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[settings] setScheduledStartEndTimeLoadingState: ${action.payload}`);
      state.scheduledStartEndTimeLoading = action.payload;
    },
  },
});

/// REDUCERS

export const {
  setSettingsState,
  setIsDarkModeState,
  setEnableDebugLogState,
  setEnableGeolocationLogState,
  setGpsServicesAvailableState,
  setDistanceTrackingUserActivatedState,
  setDistanceTrackingUserPausedState,
  setGpsWatchStartingState,
  setGpsWatchStartedState,
  setGpsServicesUserEnabledState,
  setEnableOfflineModeState,
  //setScheduledStartTimeState,
  setScheduledEndTimeState,
  setScheduledStartEndTimeLoadingState,
} = settingsSlice.actions;

/// CUSTOM SELECTORS

export default settingsSlice.reducer;
